import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { ProtectedImage } from "@/components/ProtectedImage";
import { useAuth } from "@/context/AuthContext";
import { brl, num, fmtDateTime, locString } from "@/lib/format";
import { StatusBadge, CritBadge } from "@/components/StatusBadge";
import { LocationMap } from "@/components/LocationMap";
import { QRLabel } from "@/components/QRLabel";
import { PartActions } from "@/components/PartActions";
import { PartForm } from "@/components/PartForm";
import { ZoomableImage } from "@/components/ZoomableImage";
import { ForceDeleteDialog } from "@/components/ForceDeleteDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Package, Cog, QrCode, Pencil, Ship, TrendingUp, Info, Wrench, MapPin, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiErr } from "@/lib/api";

const Metric = ({ label, value, hint }) => (
  <div className="rounded-lg bg-slate-950/60 border border-slate-800 p-3">
    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">{label}</div>
    <div className="text-lg font-bold text-slate-100 font-mono">{value}</div>
    {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
  </div>
);

export default function PartDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, canFinance } = useAuth();
  const [p, setP] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [movs, setMovs] = useState([]);
  const [qrOpen, setQrOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [del, setDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const openDelete = async () => {
    try {
      const { data } = await api.get(`/parts/${p.id}/has-history`);
      setDel({ hasHistory: data.has_history });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const confirmDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/parts/${p.id}/permanent`, { params: del.hasHistory ? { force: true } : {} });
      toast.success("Peça excluída definitivamente.");
      navigate("/pecas");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setDelBusy(false); }
  };

  const load = useCallback(() => {
    setLoadError(null);
    api.get(`/parts/${id}`).then((r) => setP(r.data)).catch((e) => setLoadError(e?.response?.status === 404 ? "notfound" : "error"));
    api.get("/movements", { params: { part_id: id, limit: 30 } }).then((r) => setMovs(r.data)).catch(() => {});
  }, [id]);
  useEffect(load, [load]);

  if (!p && loadError) return (
    <Card className="card-metal p-8 text-center space-y-4" data-testid="part-load-error">
      <Package className="h-10 w-10 mx-auto text-slate-500" />
      <div className="text-slate-200">{loadError === "notfound" ? "Peça não encontrada. Ela pode ter sido excluída ou a etiqueta é de outra empresa." : "Não foi possível carregar a peça. Verifique a conexão e tente de novo."}</div>
      <div className="flex flex-wrap justify-center gap-2">
        <Button variant="secondary" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button>
        {loadError === "error" && <Button className="bg-sky-700 hover:bg-sky-600" onClick={load}>Tentar de novo</Button>}
      </div>
    </Card>
  );
  if (!p) return <div className="space-y-4"><Skeleton className="h-8 w-48 bg-slate-800" /><Skeleton className="h-96 bg-slate-800" /></div>;

  const m = p.metrics || {};
  const ex = p.explanation || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-400"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button data-testid="btn-open-qr" variant="secondary" onClick={() => setQrOpen(true)}><QrCode className="h-4 w-4 mr-1" /> QR Code</Button>
          {can("ALMOXARIFADO") && <Button data-testid="btn-edit-part" variant="secondary" onClick={() => setEditOpen(true)}><Pencil className="h-4 w-4 mr-1" /> Editar</Button>}
          {can("ALMOXARIFADO") && p.status === "ATIVO" && <Button data-testid="btn-inactivate-part" variant="secondary" className="text-rose-300" onClick={async () => { try { await api.delete(`/parts/${p.id}`); toast.success("Peça inativada."); load(); } catch (e) { toast.error(apiErr(e)); } }}><Power className="h-4 w-4 mr-1" /> Inativar</Button>}
          {can("ALMOXARIFADO") && p.status === "INATIVO" && !p.consolidado_em_id && <Button data-testid="btn-reactivate-part" variant="secondary" className="text-emerald-300" onClick={async () => { try { await api.put(`/parts/${p.id}/reactivate`); toast.success("Peça reativada."); load(); } catch (e) { toast.error(apiErr(e)); } }}><Power className="h-4 w-4 mr-1" /> Reativar</Button>}
          {can() && <Button data-testid="btn-delete-part" variant="secondary" className="text-rose-400" onClick={openDelete}><Trash2 className="h-4 w-4 mr-1" /> Excluir</Button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: photo + identity */}
        <Card className="card-metal p-5 lg:col-span-1">
          <div className="rounded-xl overflow-hidden bg-slate-950 aspect-square mb-4">
            {p.foto_principal ? <ZoomableImage src={p.foto_principal} alt={p.descricao} testid="part-detail-photo" className="h-full w-full object-cover" />
              : <div className="h-full flex items-center justify-center"><Package className="h-16 w-16 text-slate-700" /></div>}
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-50 leading-tight">{p.descricao}</h1>
          <div className="font-mono text-sm text-sky-400 mt-1">{p.codigo_interno}{p.status === "INATIVO" && <span className="ml-2 text-rose-400 text-xs">· INATIVA</span>}</div>
          {p.consolidado_em_id && <div className="mt-2 rounded-lg border border-amber-700 bg-amber-950/50 p-2 text-sm text-amber-200" data-testid="part-consolidated">
            Cadastro consolidado. A peça agora é controlada no <Link className="underline" to={`/pecas/${p.consolidado_em_id}`}>cadastro principal</Link>. O histórico deste código continua aqui.</div>}
          {p.codigos_alternativos?.length > 0 && <div className="mt-2 text-xs text-slate-400">Códigos antigos consolidados: {p.codigos_alternativos.map((c) => c.codigo_interno).filter(Boolean).join(", ")}</div>}
          <div className="flex flex-wrap gap-2 mt-3">
            <StatusBadge status={m.status_reposicao} full size="lg" testid="part-status-badge" />
            <CritBadge level={p.criticidade} />
            {p.origem === "IMPORTADA" && (
              <span className="inline-flex items-center gap-1 rounded-md border border-rose-800/60 bg-rose-950/40 px-2 py-0.5 text-xs text-rose-200"><Ship className="h-3.5 w-3.5" /> Importada · {p.pais_origem}</span>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Metric label="Estoque atual" value={`${num(p.quantidade_atual)} ${p.unidade}`} />
            {canFinance && <Metric label="Custo médio" value={brl(p.custo_medio)} />}
            <Metric label="Estoque mínimo" value={num(p.estoque_minimo)} />
            <Metric label="Estoque ideal" value={num(p.estoque_ideal)} />
          </div>
        </Card>

        {/* Right: actions + tabs */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="card-metal p-5">
            <h3 className="font-display text-lg font-semibold mb-3 text-slate-100">Operações rápidas</h3>
            <PartActions part={p} onDone={load} showConsult={false} />
          </Card>

          <Card className="card-metal p-5">
            <div className="flex items-center gap-2 mb-3"><MapPin className="h-4 w-4 text-sky-400" /><h3 className="font-display text-lg font-semibold text-slate-100">Localização física</h3></div>
            <LocationMap location={p.localizacao} />
          </Card>

          <Tabs defaultValue="reposicao">
            <TabsList className="bg-slate-900 border border-slate-800">
              <TabsTrigger value="reposicao" data-testid="tab-reposicao">Reposição</TabsTrigger>
              <TabsTrigger value="maquinas" data-testid="tab-maquinas">Máquinas ({p.maquinas?.length || 0})</TabsTrigger>
              <TabsTrigger value="ficha" data-testid="tab-ficha">Ficha técnica</TabsTrigger>
              <TabsTrigger value="historico" data-testid="tab-historico">Histórico</TabsTrigger>
            </TabsList>

            <TabsContent value="reposicao" className="mt-4">
              <Card className="card-metal p-5 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Metric label="Consumo médio/mês" value={num(m.consumo_medio_mensal, 1)} />
                  <Metric label="Cobertura" value={m.cobertura_meses != null ? `${num(m.cobertura_meses, 1)} m` : "—"} hint={m.cobertura_dias != null ? `${num(m.cobertura_dias)} dias` : ""} />
                  <Metric label="Ponto reposição" value={num(m.ponto_reposicao, 1)} />
                  <Metric label="Lead time" value={`${p.lead_time_dias} dias`} />
                </div>
                <div className={`rounded-xl border p-4 ${m.status_reposicao === "CRITICO" ? "border-rose-800/60 bg-rose-950/30" : m.status_reposicao === "ATENCAO" ? "border-amber-800/60 bg-amber-950/30" : "border-emerald-800/60 bg-emerald-950/20"}`}>
                  <div className="flex items-center gap-2 mb-2"><TrendingUp className="h-5 w-5 text-slate-200" /><span className="font-display text-lg font-bold text-slate-50">{ex.status_texto}</span></div>
                  <p className="text-sm text-slate-200"><b>Recomendação:</b> {ex.recomendacao}</p>
                  <p className="text-sm text-slate-400 mt-1"><b>Motivo:</b> {ex.motivo}</p>
                  {m.quantidade_sugerida > 0 && (
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <span className="rounded-md bg-slate-900 px-3 py-1 font-mono">Sugestão: <b className="text-sky-300">{m.quantidade_sugerida} un.</b></span>
                      {canFinance && m.custo_estimado != null && <span className="rounded-md bg-slate-900 px-3 py-1 font-mono">Custo est.: <b className="text-emerald-300">{brl(m.custo_estimado)}</b></span>}
                    </div>
                  )}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="maquinas" className="mt-4">
              <div className="grid sm:grid-cols-2 gap-3">
                {(p.maquinas || []).map((mq) => (
                  <Card key={mq.id} className="card-metal p-4 cursor-pointer" onClick={() => navigate(`/maquinas/${mq.id}`)}>
                    <div className="flex gap-3">
                      {mq.foto ? <ProtectedImage src={mq.foto} alt="" className="h-16 w-16 rounded object-cover" /> : <div className="h-16 w-16 rounded bg-slate-800 flex items-center justify-center"><Cog className="h-6 w-6 text-slate-600" /></div>}
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-100">{mq.nome}</div>
                        <div className="text-xs text-slate-400 flex items-center gap-1"><Wrench className="h-3 w-3" /> {mq.posicao_instalacao || "—"}</div>
                        <div className="text-xs text-slate-500 mt-1">Qtd/máquina: <b className="text-slate-300">{mq.quantidade_por_maquina}</b></div>
                      </div>
                    </div>
                    {mq.foto_instalacao && <ZoomableImage src={mq.foto_instalacao} alt="Instalação" testid={`install-photo-${mq.id}`} className="mt-3 w-full h-32 object-cover rounded" />}
                  </Card>
                ))}
                {(!p.maquinas || p.maquinas.length === 0) && <p className="text-sm text-slate-500">Nenhuma máquina associada.</p>}
              </div>
            </TabsContent>

            <TabsContent value="ficha" className="mt-4">
              <Card className="card-metal p-5 space-y-3 text-sm">
                {[
                  ["Descrição técnica", p.descricao_tecnica],
                  ["Categoria", p.categoria?.nome],
                  ["Fabricante", p.fabricante],
                  ["Fornecedor", p.fornecedor?.nome],
                  ["Código fornecedor", p.codigo_fornecedor],
                  ["Código Omie (futuro)", p.codigo_omie || "—"],
                  ["Estoque de segurança", num(p.estoque_seguranca)],
                  ["Observações", p.observacoes_tecnicas || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-slate-800 pb-2">
                    <span className="text-slate-500">{k}</span><span className="text-slate-200 text-right">{v || "—"}</span>
                  </div>
                ))}
              </Card>
            </TabsContent>

            <TabsContent value="historico" className="mt-4">
              <Card className="card-metal p-0 overflow-hidden">
                {movs.length === 0 ? <div className="p-6 text-sm text-slate-500">Sem movimentações.</div> : (
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[560px]">
                    <thead><tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
                      <th className="text-left px-4 py-2">Data</th><th className="text-left px-4 py-2">Tipo</th><th className="text-right px-4 py-2">Qtd</th><th className="text-left px-4 py-2">Máquina</th><th className="text-left px-4 py-2">Usuário</th>
                    </tr></thead>
                    <tbody>
                      {movs.map((mv) => (
                        <tr key={mv.id} className="border-t border-slate-800">
                          <td className="px-4 py-2 text-slate-400">{fmtDateTime(mv.data)}</td>
                          <td className="px-4 py-2"><TypeTag t={mv.tipo} /></td>
                          <td className="px-4 py-2 text-right font-mono text-slate-200">{mv.quantidade}</td>
                          <td className="px-4 py-2 text-slate-300">{mv.machine_nome || "—"}</td>
                          <td className="px-4 py-2 text-slate-400">{mv.usuario_nome}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader><DialogTitle className="font-display text-2xl">Etiqueta QR Code</DialogTitle></DialogHeader>
          <QRLabel part={p} />
          <p className="text-xs text-slate-500 text-center">Aponte a câmera do tablet/celular para abrir esta ficha diretamente.</p>
        </DialogContent>
      </Dialog>

      <PartForm open={editOpen} onOpenChange={setEditOpen} part={p} onSaved={load} />
      <ForceDeleteDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} entityLabel="a peça" name={p.descricao} hasHistory={del?.hasHistory} busy={delBusy} onConfirm={confirmDelete} />
    </div>
  );
}

function TypeTag({ t }) {
  const map = {
    ENTRADA: "bg-emerald-950/60 text-emerald-300",
    RETIRADA: "bg-rose-950/60 text-rose-300",
    DEVOLUCAO: "bg-sky-950/60 text-sky-300",
    AJUSTE: "bg-amber-950/60 text-amber-300",
    INVENTARIO: "bg-purple-950/60 text-purple-300",
  };
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${map[t] || "bg-slate-800 text-slate-300"}`}>{t}</span>;
}
